import React, { useEffect, useState, MutableRefObject } from 'react';
import { connect } from 'react-redux';

import { showModal } from 'actions/ModalActions';
import {
  getHomepageSections,
  editHomepageSection,
} from 'actions/website/entities/HomepageSectionActions';
import { resetEntities, addEntity } from 'actions/website/EntityActions';

import WebsiteVideoUploadModal from 'components/manage/website/WebsiteVideoUploadModal';
import WebsiteVideoUpload from 'components/manage/website/WebsiteVideoUpload';

import type {
  WCmsHomepageSectionView,
  WCmsPageView,
  WCmsVideoView,
} from '@zola/svc-web-api-ts-client';
import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type { RootState, AppDispatch } from 'reducers/index';
import { entityNames } from 'actions/PublicWebsiteActions';

// util
import { filterEntitiesById, filterEntitiesOrderedIds } from 'util/filterEntities';

// Components
import EditPageHeader from '../EditPageCommon/EditPageHeader/EditPageHeader';
import WeddingDetailsAccordion from './components/WeddingDetailsAccordion';
import HomeSettingsForm from './components/HomeSettingsForm';
import MonogramFooter from './components/MonogramFooter';
import PaperPreviewCrossSellModule from '../../PaperPreviewCrossSellModule';
import EditPageAddEntity from '../EditPageCommon/EditPageAddEntity';
import HomeImageUpload from './components/HomeImageUpload';
import { EntityName } from '../EditPageCommon/EditPageSortableList';
import EditPageEntityDetail from '../EditPageCommon/EditPageSortableList/EditPageEntityDetail';
import { VideoDetails, VideoCaption } from '../EditPageCommon/entityDetails/VideoDetails';

import sharedStyles from '../EditPageCommon/editPageSharedStyles.module.less';

type HomeProps = HomeStateProps & HomeDispatchProps;

const Home: React.FC<HomeProps> = ({
  entities,
  orderedIds,
  page,
  themeKey,
  getHomepageSectionEntities,
  unloadEntities,
  onEdit,
  openModal,
}) => {
  const homepageSections = filterEntitiesById(entities, entityNames.home);
  const homeOrderedIds = filterEntitiesOrderedIds(orderedIds, entities, entityNames.home);
  const videos = filterEntitiesById(entities, entityNames.videos);
  const videoOrderedIds = filterEntitiesOrderedIds(orderedIds, entities, entityNames.videos);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<GenericEntityType | undefined>();
  const [triggeringRef, setTriggeringRef] = useState<MutableRefObject<null> | null>(null);

  useEffect(() => {
    getHomepageSectionEntities();
    return () => unloadEntities();
  }, []);

  const handleToggleModal = () => {
    if (modalOpen) {
      if (editingEntity) setEditingEntity(undefined);
      setTriggeringRef(null);
    }
    setModalOpen(!modalOpen);
  };

  const openEditVideoEntityModal = (entity: GenericEntityType, ref: MutableRefObject<null>) => {
    handleToggleModal();
    setTriggeringRef(ref);
    setEditingEntity(entity);
  };

  const StoryDetails = (entity: WCmsHomepageSectionView): JSX.Element => {
    const story = entity || {};
    const { title } = story;
    return <EntityName name={title} />;
  };

  return (
    <div>
      {themeKey && <EditPageHeader onAdd={openModal} page={page} themeKey={themeKey} />}
      <WeddingDetailsAccordion />
      <HomeSettingsForm />
      <HomeImageUpload imageKey="header" className={sharedStyles.imageUploadSpacing} />
      <PaperPreviewCrossSellModule
        themeKey={themeKey}
        photoUrl={page.header_image_url}
        trackingParams={{
          cta_id: `single_sample_website_edit_homepage`,
          location: 'WEBSITE',
          section: `WEBSITE_EDIT_HOMEPAGE`,
        }}
        showSwatchArrow
        disableResponsiveStyling={{ tablet: true }}
        fetchNextMostPopularIfNone
      />
      {videoOrderedIds.length ? (
        <div className="edit-page__add-entity-section">
          <h4 className="entities-list-title">Video</h4>
          <EditPageEntityDetail
            entity={videos[videoOrderedIds[0]]}
            entityDetailProps={{
              onEdit: openEditVideoEntityModal,
              entityDetail: VideoDetails,
              entitySecondaryDetail: VideoCaption,
              entityEditRemoveName: 'video',
            }}
            singularItem
          />
        </div>
      ) : (
        <WebsiteVideoUpload
          label="Some couples add an engagement video or slideshow."
          page="home"
        />
      )}
      <EditPageAddEntity
        entityName="Story"
        entitiesListTitle="Stories"
        pageType="home"
        orderedIds={homeOrderedIds}
        entities={homepageSections}
        onAdd={openModal}
        entityDetailProps={{
          onEdit,
          entityDetail: StoryDetails,
          entityEditRemoveName: 'story',
        }}
      />
      <HomeImageUpload imageKey="footer" className={sharedStyles.imageUploadSpacing} />
      <MonogramFooter />
      {modalOpen && (
        <WebsiteVideoUploadModal
          onClose={handleToggleModal}
          triggerRef={triggeringRef}
          entity={editingEntity}
          page="home"
        />
      )}
    </div>
  );
};

type HomeStateProps = {
  entities: Record<number, WCmsHomepageSectionView | WCmsVideoView>;
  orderedIds: number[];
  page: WCmsPageView;
  themeKey?: string;
};

const mapStateToProps = (state: RootState): HomeStateProps => ({
  entities: state.website.entities.byId,
  orderedIds: state.website.entities.orderedIds,
  page: state.website.pages.byId[state.website.pages.typeToId.HOME],
  themeKey: state.wedding.theme.key,
});

type HomeDispatchProps = {
  getHomepageSectionEntities: () => void;
  unloadEntities: () => void;
  onEdit: (entity: WCmsHomepageSectionView | WCmsVideoView) => void;
  openModal: () => void;
};

const mapDispatchToProps = (dispatch: AppDispatch): HomeDispatchProps => ({
  openModal: () => {
    dispatch(addEntity());
    dispatch(showModal('HOMEPAGE_SECTION', {}, { size: 'md' }));
  },
  getHomepageSectionEntities: () => {
    dispatch(getHomepageSections());
  },
  onEdit: faq => {
    dispatch(editHomepageSection(faq));
    dispatch(showModal('HOMEPAGE_SECTION', { isUpdate: true }, { size: 'md' }));
  },
  unloadEntities: () => {
    dispatch(resetEntities());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
